import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, PanelRightIcon } from '../ui/icons';
import { useUIStore } from '../../store/ui';
import { useSettingsStore } from '../../store/settings';
import { useMCPStore } from '../../store/mcp';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../../lib/api-config';

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function ChatHeader() {
  const { sidePanelOpen, toggleSidePanel } = useUIStore();
  const { activeInstance, setActiveInstance } = useSettingsStore();
  const { instances, setInstances } = useMCPStore();
  const navigate = useNavigate();

  const [showPicker, setShowPicker] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Load instances on mount if not already in store
  useEffect(() => {
    if (instances.length > 0 || loaded) return;
    setLoaded(true);

    const apiClient = createApiClient(API_BASE_URL);
    apiClient.getInstances().then((res) => {
      if (res.data?.instances) {
        setInstances(res.data.instances);
        // Also set active instance in settings store if not set
        if (!activeInstance) {
          const active = res.data.instances.find((i) => i.isActive);
          if (active) setActiveInstance(active);
        }
      }
    });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPicker]);

  const handleSwitch = (instanceId: string) => {
    setShowPicker(false);
    const switched = instances.find((i) => i.id === instanceId);
    if (switched) {
      setActiveInstance(switched);
    }
  };

  return (
    <header className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-3 md:px-6 md:py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
            <SparklesIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Build Workflow</h1>
            <p className="hidden md:block text-sm text-gray-500">
              Describe your automation and AI will generate the n8n workflow
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Instance Switcher */}
          <div className="relative" ref={pickerRef}>
            {instances.length > 0 ? (
              <button
                onClick={() => setShowPicker(!showPicker)}
                className="flex items-center gap-2.5 rounded-lg border px-3.5 py-2 text-sm font-semibold transition-all hover:shadow-sm"
                style={{
                  borderColor: activeInstance?.color ? `${activeInstance.color}40` : '#e5e7eb',
                  backgroundColor: activeInstance?.color ? `${activeInstance.color}0a` : '#fff',
                  color: '#374151',
                }}
              >
                <span
                  className="inline-block h-3 w-3 rounded-full ring-2 ring-white"
                  style={{ backgroundColor: activeInstance?.color || '#9ca3af' }}
                />
                <span className="hidden sm:inline max-w-[140px] truncate">
                  {activeInstance?.name || 'No instance'}
                </span>
                <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>
            ) : (
              <button
                onClick={() => navigate('/connect')}
                className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
              >
                <span className="inline-block h-2 w-2 rounded-full bg-gray-300" />
                No instance
              </button>
            )}

            {showPicker && instances.length > 0 && (
              <div className="absolute right-0 z-20 mt-1.5 w-60 rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl">
                {instances.map((inst) => (
                  <button
                    key={inst.id}
                    onClick={() => handleSwitch(inst.id)}
                    className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm transition-colors ${
                      inst.id === activeInstance?.id
                        ? 'font-semibold text-gray-900 bg-gray-50'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span
                      className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: inst.color || '#9ca3af' }}
                    />
                    <span className="flex-1 truncate">{inst.name}</span>
                    {inst.id === activeInstance?.id && (
                      <CheckIcon className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                ))}
                <div className="border-t border-gray-100 mt-1.5 pt-1">
                  <button
                    onClick={() => {
                      setShowPicker(false);
                      navigate('/connect');
                    }}
                    className="flex w-full items-center px-3.5 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                  >
                    Manage instances
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={toggleSidePanel}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              sidePanelOpen
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <PanelRightIcon className="h-5 w-5" />
            <span className="hidden md:inline">Preview Panel</span>
          </button>
        </div>
      </div>
    </header>
  );
}
